'use client';

/**
 * Pixel-art sheep grazing in a meadow strip. Replaces the
 * "FROM THE DESK → 0 ON THE NEEDLES, 0 IN THE BIN." text band on
 * the home page (Veronika asked 2026-05-08 to ditch that text and
 * reuse the strip for a small calming animation à la the Chrome
 * offline T-Rex).
 *
 * Sheep silhouette + scattered ground dots as inline SVG (pure
 * rectangles, pixel-accurate, ~3KB on the wire). Behaviour:
 * - Walks slowly across the full screen width (60s per cycle).
 * - Stops twice mid-walk to graze (head dips, body holds still
 *   for several seconds).
 * - Each new cycle has a 30% chance to flip direction — sometimes
 *   the sheep arrives from the other side. Sprite mirrors so it
 *   always faces the way it's walking.
 * - Loops forever. CSS keyframes for movement + grazing, a small
 *   useEffect with `animationiteration` event listener for the
 *   direction-flip dice roll.
 * - Honors `prefers-reduced-motion: reduce` (animation disabled).
 */

import { useEffect, useRef, useState } from 'react';

const SHEEP_INK = 'var(--ink)'; // matches B1.ink

function Llama() {
  // Pixel llama — side-profile silhouette. Build 1123 reverted
  // to the v1 design (single ear, square head, vertical neck,
  // wide rectangular body) after Veronika 2026-05-08 said "the
  // old llama was better" comparing against the 1122 redesign.
  // ~10% of walk cycles in the meadow strip become a llama
  // instead of the default sheep.
  return (
    <svg
      width="32"
      height="44"
      viewBox="0 0 14 22"
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      aria-hidden
    >
      {/* Single ear — sticks up from top of head */}
      <rect x="1" y="0" width="1" height="2" fill={SHEEP_INK} />

      {/* Head — square block, sits forward (left) of the neck */}
      <g className="sheep-head">
        <rect x="0" y="2" width="4" height="3" fill={SHEEP_INK} />
        {/* eye */}
        <rect x="1" y="3" width="1" height="1" fill="var(--paper-1)" />
      </g>

      {/* Long thin vertical neck */}
      <rect x="1" y="5" width="2" height="6" fill={SHEEP_INK} />

      {/* Tail bump — small block sticking up from back-top of body */}
      <rect x="11" y="9" width="1" height="2" fill={SHEEP_INK} />

      {/* Body — wider rectangle */}
      <rect x="0" y="11" width="13" height="4" fill={SHEEP_INK} />

      {/* Four separated stilt legs — 1px wide per Veronika
          2026-05-08 (build 1121, "the legs can be thinner").
          Spacing nudged so the four stilts read as evenly
          distributed at the new thinner weight. */}
      <rect x="1" y="15" width="1" height="7" fill={SHEEP_INK} />
      <rect x="4" y="15" width="1" height="7" fill={SHEEP_INK} />
      <rect x="9" y="15" width="1" height="7" fill={SHEEP_INK} />
      <rect x="12" y="15" width="1" height="7" fill={SHEEP_INK} />
    </svg>
  );
}

/**
 * The sheep's pixels, as [x, y, width, height] rectangles on a 32×20 grid.
 */
export const SHEEP_PIXELS = {
  /** Cloud-like body, then the four legs and the tail. The legs are 4 thin
   *  rectangles; the walking bob in CSS makes them look like they're moving
   *  even though they don't alternate, which keeps the sheep slow + calming. */
  body: [
    [8, 3, 20, 9], [9, 1, 4, 2], [14, 0, 4, 3], [19, 1, 4, 2], [24, 2, 3, 1],
    [9, 12, 2, 6], [13, 12, 2, 6], [20, 12, 2, 6], [24, 12, 2, 6],
    [27, 4, 1, 2],
  ],
  /** Head sticking out left, with its tiny ear. Grouped so it can dip to graze. */
  head: [[2, 6, 6, 6], [6, 4, 2, 2]],
  /** Light pixel on the dark head. */
  eye: [4, 7] as const,
};

export function Sheep({ width = 32 }: { width?: number } = {}) {
  const rect = ([x, y, w, h]: number[], i: number) => (
    <rect key={i} x={x} y={y} width={w} height={h} fill={SHEEP_INK} />
  );
  return (
    <svg
      width={width}
      height={(width * 20) / 32}
      viewBox="0 0 32 20"
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      aria-hidden
    >
      {SHEEP_PIXELS.body.map(rect)}
      <g className="sheep-head">
        {SHEEP_PIXELS.head.map(rect)}
        <rect x={SHEEP_PIXELS.eye[0]} y={SHEEP_PIXELS.eye[1]} width="1" height="1" fill="var(--paper-1)" />
      </g>
    </svg>
  );
}

function GrassDot({ size = 1 }: { size?: number }) {
  // Veronika 2026-05-08: replaced the tuft pattern with a single
  // pixel dot — simpler, less busy, reads as "scattered grain on
  // the meadow floor" rather than literal grass tufts.
  return (
    <svg
      width={2 * size}
      height={2 * size}
      viewBox="0 0 2 2"
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      aria-hidden
    >
      <rect x="0" y="0" width="2" height="2" fill={SHEEP_INK} />
    </svg>
  );
}

// Custom event broadcast on the document whenever the meadow's
// foreground walker changes direction. Build 1126 — Veronika
// 2026-05-08: "the animal will always arrive from the opposite
// side then the upper 1st plan line". The thin strip listens
// for this event and mirrors the inverse so the two herds always
// walk toward each other (or away from each other), never together.
// Using a DOM custom event instead of a React callback prop
// because Next.js 16 flags non-serializable function props on
// `'use client'` components — this avoids the cross-component
// function-prop pattern entirely.
export const MEADOW_FG_FLIPPED_EVENT = 'knitlib:meadow-fg-flipped';

export function SheepStrip() {
  const walkerRef = useRef<HTMLDivElement | null>(null);
  const bgWalkerRef = useRef<HTMLDivElement | null>(null);
  // Which animal is walking this cycle on each plane. Sheep most
  // of the time; llama drops in ~10% of cycles as a rare visitor
  // (Veronika 2026-05-08). State persists across re-renders; only
  // re-rolled at the end of each completed walk cycle so we don't
  // flicker mid-animation.
  const [animalKind, setAnimalKind] = useState<'sheep' | 'llama'>('sheep');
  // 2026-05-08 Veronika: "We can have 2 plans within the animation.
  // First plan is closer the sheep is bigger, second plan more far
  // — sheep is smaller". Background plane walks higher in the strip,
  // smaller scale, lower opacity, slower walk cycle (parallax).
  const [bgAnimalKind, setBgAnimalKind] = useState<'sheep' | 'llama'>('sheep');
  // Foreground walker's direction state — lifted into React (was
  // a pure DOM class toggle) so the thin strip below the cards
  // can mirror its inverse via the broadcast event.
  const [fgFlipped, setFgFlipped] = useState(false);

  // Broadcast direction changes for the thin strip to mirror.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.dispatchEvent(
      new CustomEvent(MEADOW_FG_FLIPPED_EVENT, { detail: { flipped: fgFlipped } }),
    );
  }, [fgFlipped]);

  // At each completed walk cycle (per plane):
  //  - 30% dice roll flips the direction class. Reversing the
  //    keyframe makes the animal enter from the OTHER edge, and
  //    the `.flipped` modifier mirrors the sprite horizontally.
  //  - 10% dice roll swaps in the llama. Otherwise sheep.
  useEffect(() => {
    const fg = walkerRef.current;
    const bg = bgWalkerRef.current;
    const cleanups: Array<() => void> = [];

    if (fg) {
      const onFgIter = (e: AnimationEvent) => {
        if (e.animationName !== 'sheep-walk') return;
        if (Math.random() < 0.3) setFgFlipped((prev) => !prev);
        setAnimalKind(Math.random() < 0.1 ? 'llama' : 'sheep');
      };
      fg.addEventListener('animationiteration', onFgIter);
      cleanups.push(() => fg.removeEventListener('animationiteration', onFgIter));
    }

    if (bg) {
      // Background walker still does its own DOM class-toggle for
      // direction — its direction is independent of the fg walker
      // (Veronika hasn't tied the bg plane to anything yet) so no
      // need to lift it into React state.
      const onBgIter = (e: AnimationEvent) => {
        if (e.animationName !== 'sheep-walk-bg') return;
        if (Math.random() < 0.3) bg.classList.toggle('flipped');
        setBgAnimalKind(Math.random() < 0.1 ? 'llama' : 'sheep');
      };
      bg.addEventListener('animationiteration', onBgIter);
      cleanups.push(() => bg.removeEventListener('animationiteration', onBgIter));
    }
    return () => cleanups.forEach((c) => c());
  }, []);

  // Static dot positions across the strip — placed at percentages
  // so they redistribute on resize. Sizes vary slightly to feel
  // hand-placed, not regimented. (Replaced grass tufts with simple
  // dots per Veronika's ask 2026-05-08.)
  const grassPositions: Array<{ leftPct: number; bottomPx: number; size: number }> = [
    { leftPct: 6, bottomPx: 6, size: 1 },
    { leftPct: 14, bottomPx: 10, size: 1 },
    { leftPct: 22, bottomPx: 4, size: 1.5 },
    { leftPct: 30, bottomPx: 12, size: 1 },
    { leftPct: 40, bottomPx: 6, size: 1 },
    { leftPct: 48, bottomPx: 14, size: 1 },
    { leftPct: 58, bottomPx: 4, size: 1.5 },
    { leftPct: 67, bottomPx: 8, size: 1 },
    { leftPct: 76, bottomPx: 12, size: 1 },
    { leftPct: 84, bottomPx: 4, size: 1.5 },
    { leftPct: 92, bottomPx: 10, size: 1 },
  ];
  // Background plane grass — smaller dots positioned around the
  // bg sheep's walking line (bottom: ~30). Veronika 2026-05-08:
  // "in the 2nd plan I am missing smaller dots for grass". Sizes
  // are 0.5 so the dots are 1x1px (vs 2x2px on the floor) — reads
  // as distant grain.
  const bgGrassPositions: Array<{ leftPct: number; bottomPx: number; size: number }> = [
    { leftPct: 8, bottomPx: 28, size: 0.5 },
    { leftPct: 19, bottomPx: 32, size: 0.5 },
    { leftPct: 28, bottomPx: 30, size: 0.5 },
    { leftPct: 37, bottomPx: 33, size: 0.5 },
    { leftPct: 45, bottomPx: 28, size: 0.5 },
    { leftPct: 54, bottomPx: 31, size: 0.5 },
    { leftPct: 62, bottomPx: 29, size: 0.5 },
    { leftPct: 71, bottomPx: 33, size: 0.5 },
    { leftPct: 80, bottomPx: 28, size: 0.5 },
    { leftPct: 88, bottomPx: 31, size: 0.5 },
    { leftPct: 95, bottomPx: 29, size: 0.5 },
  ];
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
      aria-hidden
    >
      {/* Background plane grass — small dots around the bg sheep's
          walking line. Renders first so it sits behind the bg sheep
          if their boxes overlap. */}
      {bgGrassPositions.map((g, i) => (
        <div
          key={`bg-${i}`}
          style={{
            position: 'absolute',
            left: `${g.leftPct}%`,
            bottom: g.bottomPx,
            opacity: 0.55,
          }}
        >
          <GrassDot size={g.size} />
        </div>
      ))}

      {/* Foreground plane grass — bigger dots scattered along the
          bottom of the strip where the fg sheep walks. */}
      {grassPositions.map((g, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${g.leftPct}%`,
            bottom: g.bottomPx,
            opacity: 0.55,
          }}
        >
          <GrassDot size={g.size} />
        </div>
      ))}

      {/* Background plane sheep — smaller, higher up, slower walk.
          Veronika 2026-05-08 (build 1119): "second plan more far —
          sheep is smaller", and (build 1120): "I want everything
          also black (not grey) as the 1st plan is" — opacity removed
          so the bg sheep is solid B1.ink, depth comes purely from
          size + position now. */}
      <div
        ref={bgWalkerRef}
        className="sheep-walker-bg"
        style={{
          position: 'absolute',
          bottom: 30,
          left: 0,
          willChange: 'transform',
        }}
      >
        {bgAnimalKind === 'llama' ? <Llama /> : <Sheep />}
      </div>

      {/* Foreground plane — full size, on the meadow floor.
          Animated via CSS keyframes. Walks right to left by default;
          useEffect above flips direction at ~30% of cycles. Slow +
          calming pace (60s per traversal, with 2 grazing stops baked
          into the keyframes). */}
      <div
        ref={walkerRef}
        className={`sheep-walker${fgFlipped ? ' flipped' : ''}`}
        style={{
          position: 'absolute',
          bottom: 4,
          left: 0,
          willChange: 'transform',
        }}
      >
        {animalKind === 'llama' ? <Llama /> : <Sheep />}
      </div>

      <style jsx>{`
        .sheep-walker {
          animation: sheep-walk 60s linear infinite;
          /* Veronika 2026-05-08: "When I open the app, can the
             sheep's be directly visible?" — negative delay starts
             the animation 30s into the 60s cycle, so on first
             paint the fg sheep is mid-walk around the middle of
             the screen instead of off-screen right. */
          animation-delay: -30s;
        }
        /* Background plane: 95s walk cycle (slower → parallax
           illusion of distance), no grazing stops (a distant
           sheep doesn't read as eating in detail), constant
           scale 0.55x baked into the keyframes so it doesn't
           fight with translateX. */
        .sheep-walker-bg {
          animation: sheep-walk-bg 95s linear infinite;
          /* Different start offset than the fg so the two planes
             aren't visually overlapping on first paint. -65s
             puts the bg sheep ~70% across (closer to the left
             edge while fg is in the middle). */
          animation-delay: -65s;
        }

        /* The .flipped class reverses the walk (sheep enters from
           the opposite edge) and mirrors the sprite so it faces
           the right way. The body-bob and head-graze animations
           live on inner elements with their own transforms, so
           the parent's scaleX doesn't fight with their bobbing. */
        .sheep-walker.flipped {
          animation-direction: reverse;
        }
        /* The flipped variant of sheep-bob bakes scaleX(-1) INTO
           the keyframes so it survives the animation's transform
           updates. Without this, the static transform: scaleX(-1)
           rule below was overwritten by the running sheep-bob
           keyframes — the sprite walked the new direction but the
           BODY stayed facing the old direction (tail-first walking,
           which Veronika 2026-05-08 flagged: "the animal will
           always go head first"). Build 1127 fixed it. Same pattern
           as the bg + thin strips (which already had it). */
        :global(.sheep-walker.flipped > svg) {
          animation: sheep-bob-flipped 1.6s ease-in-out infinite;
        }
        .sheep-walker-bg.flipped {
          animation-direction: reverse;
        }
        /* Background sheep scaling is baked into its own bob
           animation so the static scale(0.55) doesn't get
           clobbered by the bob transform updates. The bob
           amplitude scales down with the sprite, which actually
           reads as proper depth (distant things bob less). */
        :global(.sheep-walker-bg > svg) {
          transform-origin: bottom left;
          animation: sheep-bob-bg 1.6s ease-in-out infinite;
        }
        :global(.sheep-walker-bg.flipped > svg) {
          animation: sheep-bob-bg-flipped 1.6s ease-in-out infinite;
        }

        :global(.sheep-walker > svg) {
          animation: sheep-bob 1.6s ease-in-out infinite;
        }
        :global(.sheep-walker .sheep-head) {
          animation: sheep-graze 7s ease-in-out infinite;
          transform-origin: 6px 12px;
        }

        /* Walk with two grazing stops baked in. Holds at 70vw and
           30vw of the screen for ~6s each (10% of the 60s cycle).
           Veronika 2026-05-08: "the sheep can stop completely and
           only eat" — the head-graze animation continues during
           these holds so it looks like grazing in place. */
        @keyframes sheep-walk {
          0%   { transform: translateX(calc(100vw + 32px)); }
          22%  { transform: translateX(70vw); }
          32%  { transform: translateX(70vw); }
          58%  { transform: translateX(30vw); }
          68%  { transform: translateX(30vw); }
          100% { transform: translateX(-40px); }
        }
        /* Background plane: smooth slow drift, no grazing holds —
           reads as a distant moving silhouette, not a detailed
           grazer. */
        @keyframes sheep-walk-bg {
          0%   { transform: translateX(calc(100vw + 24px)); }
          100% { transform: translateX(-30px); }
        }
        @keyframes sheep-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-1px); }
        }
        /* Flipped variant — bakes scaleX(-1) in so the sprite
           horizontal-flip survives the bob's transform updates.
           Used by .sheep-walker.flipped > svg above. */
        @keyframes sheep-bob-flipped {
          0%, 100% { transform: scaleX(-1) translateY(0); }
          50%      { transform: scaleX(-1) translateY(-1px); }
        }
        /* Bg-sheep bob with scale baked in (and a flipped variant
           for when the .flipped class flips the walk direction). */
        @keyframes sheep-bob-bg {
          0%, 100% { transform: scale(0.55) translateY(0); }
          50%      { transform: scale(0.55) translateY(-1px); }
        }
        @keyframes sheep-bob-bg-flipped {
          0%, 100% { transform: scale(0.55) scaleX(-1) translateY(0); }
          50%      { transform: scale(0.55) scaleX(-1) translateY(-1px); }
        }
        /* Every ~7s the sheep dips its head down briefly to graze */
        @keyframes sheep-graze {
          0%, 70%, 100% { transform: translateY(0) rotate(0deg); }
          75%, 92%      { transform: translateY(2px) rotate(8deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          .sheep-walker,
          .sheep-walker-bg,
          :global(.sheep-walker > svg),
          :global(.sheep-walker-bg > svg),
          :global(.sheep-walker .sheep-head) {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Thin variant of the meadow strip — a single small walker on a
 * shorter strip. Veronika 2026-05-08: "Lets add one more animation
 * line which will be thinner. It will be placed between bottom line
 * and cards". Used between the card grid and the bottom nav so the
 * home page reads as bookended by two grazing strips. Single plane
 * (no parallax) since there isn't room for two; smaller scale; same
 * direction-flip + llama dice rolls as the meadow.
 */
export function ThinSheepStrip() {
  const walkerRef = useRef<HTMLDivElement | null>(null);
  const [animalKind, setAnimalKind] = useState<'sheep' | 'llama'>('sheep');
  // Direction is the INVERSE of the meadow's fg walker — Veronika
  // 2026-05-08: "the animal will always arrive from the opposite
  // side then the upper 1st plan line". Initial state is `true`
  // (flipped) because the meadow's fg starts un-flipped (right→left
  // by default), so this strip starts left→right. No own dice roll
  // for direction — we only re-roll the llama swap on iteration.
  const [forceFlipped, setForceFlipped] = useState(true);

  // Mirror the meadow fg's direction (inverted).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = (e: Event) => {
      const ev = e as CustomEvent<{ flipped: boolean }>;
      setForceFlipped(!ev.detail.flipped);
    };
    document.addEventListener(MEADOW_FG_FLIPPED_EVENT, onChange);
    return () => document.removeEventListener(MEADOW_FG_FLIPPED_EVENT, onChange);
  }, []);

  // Llama dice roll only — direction is controlled by the event listener.
  useEffect(() => {
    const el = walkerRef.current;
    if (!el) return;
    const onIteration = (e: AnimationEvent) => {
      if (e.animationName !== 'thin-sheep-walk') return;
      setAnimalKind(Math.random() < 0.1 ? 'llama' : 'sheep');
    };
    el.addEventListener('animationiteration', onIteration);
    return () => el.removeEventListener('animationiteration', onIteration);
  }, []);

  // Grass dots — fewer + smaller than the meadow strip's floor row.
  const grassPositions: Array<{ leftPct: number; bottomPx: number; size: number }> = [
    { leftPct: 8, bottomPx: 2, size: 0.5 },
    { leftPct: 22, bottomPx: 4, size: 0.5 },
    { leftPct: 36, bottomPx: 2, size: 0.5 },
    { leftPct: 50, bottomPx: 5, size: 0.5 },
    { leftPct: 65, bottomPx: 3, size: 0.5 },
    { leftPct: 80, bottomPx: 2, size: 0.5 },
    { leftPct: 92, bottomPx: 4, size: 0.5 },
  ];

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
      aria-hidden
    >
      {grassPositions.map((g, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${g.leftPct}%`,
            bottom: g.bottomPx,
            opacity: 0.55,
          }}
        >
          <GrassDot size={g.size} />
        </div>
      ))}

      <div
        ref={walkerRef}
        className={`thin-sheep-walker${forceFlipped ? ' flipped' : ''}`}
        style={{
          position: 'absolute',
          bottom: 1,
          left: 0,
          willChange: 'transform',
        }}
      >
        {animalKind === 'llama' ? <Llama /> : <Sheep />}
      </div>

      <style jsx>{`
        .thin-sheep-walker {
          animation: thin-sheep-walk 45s linear infinite;
          /* Same reasoning as the fg meadow walker — start mid-
             cycle so the thin strip's animal is visible on first
             paint. -22s puts it ~half way across. */
          animation-delay: -22s;
        }
        .thin-sheep-walker.flipped {
          animation-direction: reverse;
        }
        :global(.thin-sheep-walker > svg) {
          transform-origin: bottom left;
          animation: thin-sheep-bob 1.6s ease-in-out infinite;
        }
        :global(.thin-sheep-walker.flipped > svg) {
          animation: thin-sheep-bob-flipped 1.6s ease-in-out infinite;
        }
        @keyframes thin-sheep-walk {
          0%   { transform: translateX(calc(100vw + 24px)); }
          100% { transform: translateX(-30px); }
        }
        @keyframes thin-sheep-bob {
          0%, 100% { transform: scale(0.65) translateY(0); }
          50%      { transform: scale(0.65) translateY(-1px); }
        }
        @keyframes thin-sheep-bob-flipped {
          0%, 100% { transform: scale(0.65) scaleX(-1) translateY(0); }
          50%      { transform: scale(0.65) scaleX(-1) translateY(-1px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .thin-sheep-walker,
          :global(.thin-sheep-walker > svg) {
            animation: none;
          }
          :global(.thin-sheep-walker > svg) {
            transform: scale(0.65);
          }
        }
      `}</style>
    </div>
  );
}
